import { idDeOpcion } from './flows';

// La escena de «Ejecutá hablando»: lo que la persona pide, lo que hizo el producto y LA ENTREGA, con la
// opción que eligió en el recorrido. Va antes de «Probalo hoy» en Agencia, Tareas y Agentes: primero se ve
// el producto andando, después se prueba a mano. Molde: la escena del hero de Victoria AI (04/11/2026).
// La respuesta trae el resultado a la vista, nunca «te lo dejé en tal carpeta» (An, 04/11: «la respuesta es
// pobre porque tengo que ir a buscarla yo a todos lados»). Lo que se entrega se aprueba ahí: nada sale sin
// que la persona lo apruebe (bajada de la oferta).
// 🟠 Prototipo (04/11): pedidos, pasos y entregas son ejemplos míos, con cifras y nombres de muestra, a
// validar por An. Ningún nombre es de un cliente real.

const E = (pedido, acciones, entrega) => ({ pedido, acciones, entrega });

const POR_TAREA = { // ia_tarea → dataKey iaTarea
  // An, 04/11: «respuestas para copiar y pegar serían? eso es muy básico, no es una gran solución para bragging».
  // La escena muestra la consulta resuelta de punta a punta (precio, stock, presupuesto, derivación), no un borrador.
  mensajes: E('Resolvé las consultas que entraron ayer.',
    ['Leyó cada consulta con el historial del cliente', 'Consultó precio y stock en tu sistema', 'Armó los presupuestos y derivó lo que no le toca'],
    '31 consultas resueltas: 22 con precio y stock al día, 6 presupuestos listos para aprobar por $2,1 M y 3 derivadas a ventas con el contexto.'),
  informes: E('Armá el informe de ventas de octubre.',
    ['Abrió las planillas de ventas', 'Hizo las mismas cuentas que en septiembre', 'Lo armó con tu marca'],
    'Octubre: $18,4 M, 12 % más que septiembre. Tres clientes explican el 60 %. Bajó el mostrador y subió WhatsApp.'),
  carga: E('Pasá a la planilla las facturas que llegaron al mail.',
    ['Buscó en el mail las facturas de la semana', 'Sacó proveedor, fecha y monto de cada una', 'Las sumó a la planilla de compras'],
    '23 facturas cargadas: $5,6 M en compras. Dos no se pudieron leer: Corralón Este y Gas del Centro.'),
  coordinacion: E('Armá la semana del equipo.',
    ['Miró el calendario y lo que quedó pendiente', 'Repartió lo urgente por puesto', 'Escribió el mensaje del lunes para cada uno'],
    'Lunes: Ana cierra el informe, Juan llama a cuatro clientes y vos aprobás dos presupuestos. Los mensajes, listos.'),
  seguimiento: E('¿Cómo viene lo que quedó pendiente?',
    ['Revisó lo que hizo cada puesto esta semana', 'Separó lo hecho de lo trabado', 'Buscó lo que no tiene responsable'],
    'De 18 pendientes: 11 hechos, 4 en curso y 3 sin responsable: la compra de insumos, el alta de Pedro y el reclamo del local 3.'),
};

// En «Para mí», el seguimiento es un tracking personal (An, 04/11): tus pendientes, no los de un equipo.
const SEGUIMIENTO_PERSONAL = E('¿Cómo vengo con lo que tengo pendiente?',
  ['Revisó tus pendientes de la semana', 'Separó lo hecho de lo trabado', 'Ordenó lo que vence primero'],
  'De 12 pendientes: 7 hechos, 3 en curso y 2 que vencen mañana: el informe para el cliente y la factura de octubre.');

const POR_HABILIDAD = { // hab_q1 → dataKey habilidad
  lunes: POR_TAREA.coordinacion,
  bandeja: E('Ordená el mail y respondé lo que se pueda.',
    ['Separó lo urgente, lo que espera y lo que sobra', 'Respondió las consultas de siempre en tu tono', 'Apartó lo que necesita tu decisión'],
    '42 mails: 21 respuestas listas, 15 al archivo y 6 urgentes. Tres necesitan tu decisión y están arriba de todo.'),
  // Lo que importa es el efectivo del día a día: qué entra hoy y la semana que viene (An, 05/11), no la lista de deudores.
  cobranzas: E('¿Qué tengo que cobrar hoy y la semana que viene?',
    ['Cruzó las ventas a cuenta con los pagos', 'Ordenó lo que vence hoy y la semana que viene', 'Escribió un recordatorio amable para cada cliente'],
    'Hoy tendrías que cobrar $860 mil de tres clientes. La semana que viene, $2,3 M de cuatro. Los recordatorios, listos.'),
  presupuesto: E('Armá un presupuesto de 2 toneladas de hierro del 10, puesto en Villa Allende.',
    ['Leyó tu lista de precios y tus condiciones', 'Sumó el flete a Villa Allende', 'Lo armó con tu marca'],
    'Hierro del 10, 2 t: $3.480.000 más flete $95.000. Total $3.575.000, válido por 7 días.'),
  resenas: E('Respondé las reseñas nuevas de Google.',
    ['Encontró cinco reseñas nuevas', 'Respondió cada una en el tono de tu empresa', 'Cuidó especialmente la negativa'],
    'Cinco respuestas listas. La de 2 estrellas: «Lamentamos la demora, Carlos. Te escribimos hoy para resolverlo.»'),
  publicar: E('Prepará las publicaciones de la semana.',
    ['Miró qué se publicó el mes pasado', 'Pensó tres posteos con tu marca', 'Escribió los textos y sugirió las imágenes'],
    'Lunes, la promo de temporada. Miércoles, un cliente contento. Viernes, cómo se arma un pedido. Textos listos.'),
};

const POR_AREA = { // agentes_q1 → dataKey agenteTareas
  administracion: E('¿Qué hay que pagar esta semana?',
    ['Revisó vencimientos y saldos', 'Ordenó los pagos por fecha', 'Buscó lo que falta para pagar'],
    'Esta semana: $2,7 M en cinco pagos. El jueves vence ARCA ($910 mil). Falta la factura de Transportes Río.'),
  consultas: E('Atendé las consultas que entraron hoy.',
    ['Respondió las preguntas de siempre', 'Anotó a cada cliente con lo que pidió', 'Derivó lo que no sabía, con el contexto'],
    'Hoy: 31 consultas, 27 respondidas y 4 derivadas a Laura. Lo más pedido: horarios y envíos.'),
  ventas: E('Seguí a los clientes que pidieron presupuesto.',
    ['Buscó los presupuestos sin respuesta', 'Escribió un seguimiento para cada uno', 'Marcó a quiénes conviene llamar'],
    'Nueve presupuestos sin respuesta: nueve seguimientos listos. Conviene llamar a dos: pasan el millón.'),
};

// Quién responde en la escena es el producto, nunca la marca del modelo de debajo (An, 04/11: «reemplacemos
// claude por agencia, agentes o tareas según corresponda; nosotros sabemos que es claude»).
// La escena pública de cada tarea del catálogo que tiene una (catalogo.js, `demo`).
const POR_CATALOGO = { ventas: POR_TAREA.informes, cobranzas: POR_HABILIDAD.cobranzas, resenas: POR_HABILIDAD.resenas };
export const escenaDeCatalogo = (tarea) => (POR_CATALOGO[tarea] ? { quien: 'Tu agencia', ...POR_CATALOGO[tarea] } : null);

export const escenaPara = (producto, collected = {}) => {
  if (producto === 'ia') {
    const id = idDeOpcion('ia_tarea', collected.iaTarea);
    const base = id === 'seguimiento' && collected.para === 'personal' ? SEGUIMIENTO_PERSONAL : POR_TAREA[id];
    return { quien: 'Tu agencia', ...(base || POR_TAREA.informes) };
  }
  if (producto === 'habilidades') return { quien: 'La tarea', ...(POR_HABILIDAD[idDeOpcion('hab_q1', collected.habilidad)] || POR_HABILIDAD.presupuesto) };
  if (producto === 'agentes') return { quien: 'Tu agente', ...(POR_AREA[idDeOpcion('agentes_q1', collected.agenteTareas)] || POR_AREA.administracion) };
  return null;
};

// La versión corta para la primera pantalla: el pedido y la entrega, alternando entre tareas distintas
// (prueba de «Ejecutá hablando» antes de elegir nada). Sale de las mismas escenas: no hay ejemplos aparte.
export const EN_VIVO = [POR_TAREA.informes, POR_HABILIDAD.cobranzas, POR_TAREA.mensajes, POR_AREA.administracion]
  .map(({ pedido, entrega }) => ({ pedido, entrega }));

// El registro de tareas de ejemplo que se abre desde la entrega: lo recién pedido queda «Para aprobar» y
// arriba de lo que ya salió. Muestra que todo queda anotado y se sigue en un lugar (An, 04/11: «un link
// para ir al tablero o registro de tareas»). 🟠 Filas de muestra; el tablero real del cliente no existe acá.
const YA_HECHAS = [
  { hora: '09:15', pedido: POR_TAREA.informes.pedido, estado: 'Aprobado' },
  { hora: '08:50', pedido: POR_AREA.administracion.pedido, estado: 'Aprobado' },
  { hora: 'Ayer', pedido: POR_HABILIDAD.resenas.pedido, estado: 'Aprobado' },
];
export const registroPara = (escena) => [
  { hora: '10:42', pedido: escena.pedido, estado: 'Para aprobar', quien: escena.quien },
  ...YA_HECHAS.filter(f => f.pedido !== escena.pedido).slice(0, 2).map(f => ({ ...f, quien: escena.quien })),
];
